import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPromoteSeoV2LlmsTxt from './promote-seo-v2-llms-txt-llms-txt.http.js';

export interface LlmsTxt {
  /**
   * CFull text content of the `llms.txt` file.
   * @maxLength 720000
   */
  content?: string;
  /** Whether this `llms.txt` file uses Wix's default content. */
  default?: boolean;
  /**
   * Target subdomain for the `llms.txt` file (for example, 'www', 'es', 'fr'). Default: 'www'
   * @maxLength 63
   */
  subdomain?: string;
  /**
   * Whether the llms.txt file should be hidden from public access.
   * @readonly
   */
  hidden?: boolean | null;
  /** Whether the content was manually edited by the user. If `true` in DB, update/append requests must also set `manually_edited=true` or they'll be rejected with `FAILED_PRECONDITION`. */
  manuallyEdited?: boolean;
  /**
   * ISO 639-1 language code detected for the site (e.g. en, fr). Used to localize the MCP documentation section when serving llms.txt.
   * @maxLength 2
   */
  detectedLanguage?: string;
}

/** The request to get the Llms.txt file content */
export interface GetLlmsTxtRequest {
  /**
   * Subdomain of the requested `llms.txt file`. For example, `www`, `es`, `fr`. Default: `www`.
   * @maxLength 63
   */
  subdomain?: string;
}

/** The response of the Llms.txt file request */
export interface GetLlmsTxtResponse {
  /** Retrieved `llms.txt` file entity. */
  llmsTxt?: LlmsTxt;
}

/** The request to update the content of the Llms.txt file */
export interface UpdateLlmsTxtRequest {
  /** `llms.txt` file to update. */
  llmsTxt?: LlmsTxt;
}

export interface UpdateLlmsTxtResponse {
  /** Updated Llms txt object */
  llmsTxt?: LlmsTxt;
}

/** The request to append the content of the Llms.txt file */
export interface AppendLlmsTxtRequest {
  /** `llms.txt` file to append. */
  llmsTxt?: LlmsTxt;
}

export interface AppendLlmsTxtResponse {
  /** Appended `llms.txt` file. */
  llmsTxt?: LlmsTxt;
}

/** The request to update the hidden state of Llms.txt file */
export interface UpdateLlmsTxtHiddenStatusRequest {
  /**
   * Hidden state to set for the Llms.txt file.
   * If true, the llms.txt file will be hidden from public access (returns 404).
   * If false, the llms.txt file will be publicly accessible.
   */
  hidden?: boolean;
  /**
   * Subdomain of Llms.txt, for example `www`, `es`, `fr`. Default is `www`.
   * @maxLength 63
   */
  subdomain?: string;
}

/** The response of updating the hidden state of Llms.txt file */
export interface UpdateLlmsTxtHiddenStatusResponse {
  /** Updated Llms txt object with new hidden state */
  llmsTxt?: LlmsTxt;
}

/** The request to update the manually_edited state of Llms.txt file */
export interface UpdateLlmsTxtManuallyEditedStatusRequest {
  /**
   * Manually edited state to set for the Llms.txt file.
   * If true, the llms.txt file is marked as manually edited, and update/append operations
   * that do not acknowledge the flag will be rejected.
   * If false, the llms.txt file is unlocked for auto-update operations.
   */
  manuallyEdited?: boolean;
  /**
   * Subdomain of Llms.txt, for example `www`, `es`, `fr`. Default is `www`.
   * @maxLength 63
   */
  subdomain?: string;
}

/** The response of updating the manually_edited state of Llms.txt file */
export interface UpdateLlmsTxtManuallyEditedStatusResponse {
  /** Updated Llms.txt file with new manually_edited state. */
  llmsTxt?: LlmsTxt;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at `16:00` and then again at `16:01`, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

/**
 * Retrieves the `llms.txt` file entity.
 * @public
 * @permissionId PROMOTE.LLMS_TXT_READ
 * @applicableIdentity APP
 * @returns The response of the Llms.txt file request
 * @fqn com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.GetLlmsTxt
 */
export async function getLlmsTxt(
  options?: GetLlmsTxtOptions
): Promise<
  NonNullablePaths<
    GetLlmsTxtResponse,
    | `llmsTxt.content`
    | `llmsTxt.default`
    | `llmsTxt.subdomain`
    | `llmsTxt.manuallyEdited`
    | `llmsTxt.detectedLanguage`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    subdomain: options?.subdomain,
  });

  const reqOpts = ambassadorWixPromoteSeoV2LlmsTxt.getLlmsTxt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { subdomain: '$[0].subdomain' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface GetLlmsTxtOptions {
  /**
   * Subdomain of the requested `llms.txt file`. For example, `www`, `es`, `fr`. Default: `www`.
   * @maxLength 63
   */
  subdomain?: string;
}

/**
 * Updates the `llms.txt` file entity.
 *
 * The content can be set to an empty string to create a blank file.
 *
 * To restore Wix's default content, set the `default` field to `true` and omit the `content` field.
 * @public
 * @permissionId PROMOTE.LLMS_TXT_WRITE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.UpdateLlmsTxt
 */
export async function updateLlmsTxt(
  options?: UpdateLlmsTxtOptions
): Promise<
  NonNullablePaths<
    UpdateLlmsTxtResponse,
    | `llmsTxt.content`
    | `llmsTxt.default`
    | `llmsTxt.subdomain`
    | `llmsTxt.manuallyEdited`
    | `llmsTxt.detectedLanguage`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    llmsTxt: options?.llmsTxt,
  });

  const reqOpts = ambassadorWixPromoteSeoV2LlmsTxt.updateLlmsTxt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { llmsTxt: '$[0].llmsTxt' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateLlmsTxtOptions {
  /** `llms.txt` file to update. */
  llmsTxt?: LlmsTxt;
}

/**
 * Appends additional content to the existing `llms.txt` file.
 *
 * The provided content will be added to the end of the current file content.
 *
 * To reset to Wix's default content instead, set `default` to `true` and omit the `content` field.
 * @public
 * @permissionId PROMOTE.LLMS_TXT_WRITE
 * @applicableIdentity APP
 * @fqn com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.AppendLlmsTxt
 */
export async function appendLlmsTxt(
  options?: AppendLlmsTxtOptions
): Promise<
  NonNullablePaths<
    AppendLlmsTxtResponse,
    | `llmsTxt.content`
    | `llmsTxt.default`
    | `llmsTxt.subdomain`
    | `llmsTxt.manuallyEdited`
    | `llmsTxt.detectedLanguage`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    llmsTxt: options?.llmsTxt,
  });

  const reqOpts = ambassadorWixPromoteSeoV2LlmsTxt.appendLlmsTxt(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { llmsTxt: '$[0].llmsTxt' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface AppendLlmsTxtOptions {
  /** `llms.txt` file to append. */
  llmsTxt?: LlmsTxt;
}

/**
 * Updates only the hidden state of the Llms.txt file.
 * Content remains unchanged - only visibility is modified.
 * @public
 * @permissionId PROMOTE.LLMS_TXT_WRITE
 * @applicableIdentity APP
 * @returns The response of updating the hidden state of Llms.txt file
 * @fqn com.wixpress.promote.seo.robots.server.v2.LlmsServiceV2.UpdateLlmsTxtHiddenStatus
 */
export async function updateLlmsTxtHiddenStatus(
  options?: UpdateLlmsTxtHiddenStatusOptions
): Promise<
  NonNullablePaths<
    UpdateLlmsTxtHiddenStatusResponse,
    | `llmsTxt.content`
    | `llmsTxt.default`
    | `llmsTxt.subdomain`
    | `llmsTxt.manuallyEdited`
    | `llmsTxt.detectedLanguage`,
    3
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    hidden: options?.hidden,
    subdomain: options?.subdomain,
  });

  const reqOpts =
    ambassadorWixPromoteSeoV2LlmsTxt.updateLlmsTxtHiddenStatus(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          hidden: '$[0].hidden',
          subdomain: '$[0].subdomain',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateLlmsTxtHiddenStatusOptions {
  /**
   * Hidden state to set for the Llms.txt file.
   * If true, the llms.txt file will be hidden from public access (returns 404).
   * If false, the llms.txt file will be publicly accessible.
   */
  hidden?: boolean;
  /**
   * Subdomain of Llms.txt, for example `www`, `es`, `fr`. Default is `www`.
   * @maxLength 63
   */
  subdomain?: string;
}
